import { FC, Fragment } from "react";
import { graphql, Link as GatsbyLink, useStaticQuery } from "gatsby";
import { ShiIcon, Spacer } from "src/atoms";
import { Link } from "src/atoms/PrismicLink";
import ShiButton from "src/atoms/ShiButton";
import {
  Container,
  IconButton,
  Flex,
  useDisclosure,
  HStack,
  Button,
  chakra,
  Image,
} from "@chakra-ui/react";
import useHeaderDropdownState, { MenuContext } from "./state";
import { useInView } from "react-intersection-observer";
import SiteHeaderSection from "./slices/HeaderSection";
import MobileDrawer from "./components/MobileDrawer";
import {
  groupSections,
  groupSectionsV3,
  groupSectionsV5,
  isHeroWithMatchingButton,
} from "./utils";
import SiteBanner from "./components/SiteBanner";
import { onClickNewAccount, onClickStartWithLink } from "src/lib/event";
import useVisitorContext from "src/providers/VisitorContext/useVisitorContext";
import { useLocation } from "@reach/router";
import { ClickInfo } from "src/lib/event/types";
import { linkResolver, PrismicDocuemntLink } from "src/lib/linkResolver";
import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";


export type SiteNavbarV2Data = NonNullable<
  Gatsby.SiteNavbarDataQuery["prismicSiteNavbarV2"]
>["data"];
export type SiteNavbarV3Data = NonNullable<
  Gatsby.SiteNavbarDataQuery["prismicSiteNavbarV3"]
>["data"];
export type SiteNavbarV4Data = NonNullable<
  Gatsby.SiteNavbarDataQuery["prismicSiteNavbarV4"]
>["data"];
export type SiteNavbarV5Data = NonNullable<
  Gatsby.SiteNavbarDataQuery["prismicSiteNavbarV5"]
>["data"];

export type NavbarData =
  | { __typename: "SiteNavbarV2"; data: SiteNavbarV2Data }
  | { __typename: "SiteNavbarV3"; data: SiteNavbarV3Data }
  | { __typename: "SiteNavbarV4"; data: SiteNavbarV4Data }
  | { __typename: "SiteNavbarV5"; data: SiteNavbarV5Data };

export type SiteNavbarData = NavbarData["data"];

type AccountSegmentNavbarLogo = {
  url: string;
  alt?: string;
};

type SiteNavbarProps = {
  version?: "v2" | "v3" | "v4" | "v5";
  firstSlice?: Gatsby.PrismicSliceMetadataFragment | null;
  startAnchorId?: string;
  isSticky?: boolean;
  accountSegmentNavbarLogo?: AccountSegmentNavbarLogo;
};

const SiteNavbar: FC<SiteNavbarProps> = ({
  version = "v2",
  isSticky = true,
  accountSegmentNavbarLogo,
  firstSlice,
  startAnchorId,
}) => {
  const navbarQuery = useStaticQuery<Gatsby.SiteNavbarDataQuery>(graphql`
    query SiteNavbarData {
      prismicSiteNavbarV2 {
        ...SiteNavbarV2
      }
      prismicSiteNavbarV3 {
        ...SiteNavbarV3
      }
      prismicSiteNavbarV4 {
        ...SiteNavbarV4
      }
      prismicSiteNavbarV5 {
        ...SiteNavbarV5
      }
    }
  `);

  const data: SiteNavbarData | undefined =
    version === "v5"
      ? navbarQuery.prismicSiteNavbarV5?.data ||
        navbarQuery.prismicSiteNavbarV2?.data
      : version === "v4"
      ? navbarQuery.prismicSiteNavbarV4?.data ||
        navbarQuery.prismicSiteNavbarV2?.data
      : version === "v3"
      ? navbarQuery.prismicSiteNavbarV3?.data ||
        navbarQuery.prismicSiteNavbarV2?.data
      : navbarQuery.prismicSiteNavbarV2?.data;

  const visitorContext = useVisitorContext();
  const location = useLocation();

  const { ref, inView: scrollTop } = useInView({
    initialInView: true,
    rootMargin: "0px",
  });

  const drawer = useDisclosure();
  const { state, dispatch } = useHeaderDropdownState();

  type SiteBannerSlice = {
    __typename?: string;
    primary?: Record<string, any>;
    [key: string]: any;
  };
  const banner = (
    data && "body" in data ? (data.body as SiteBannerSlice[]) : undefined
  )?.find((slice: SiteBannerSlice) =>
    slice?.__typename?.includes("SiteBanner"),
  ) as Gatsby.SiteNavbarV2SiteBannerFragment | undefined;
  const headerSections =
    version === "v4"
      ? []
      : version === "v5"
      ? groupSectionsV5(data)
      : version === "v3"
      ? groupSectionsV3(
          data as NonNullable<Gatsby.SiteNavbarV3Fragment["data"]>,
        )
      : groupSections(data as NonNullable<Gatsby.SiteNavbarV2Fragment["data"]>);

  const [isFirstSliceInView, setIsFirstSliceInView] = useState(true);

  useEffect(() => {
    const el = document.getElementById("first-slice");
    if (!el) {
      setIsFirstSliceInView(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFirstSliceInView(entry.isIntersecting);
      },
      // Start the intersection box 320px from the top (below the 64px navbar).
      // This makes the Nav CTA trigger slightly earlier, giving its 0.2s fade-in animation
      // time to complete exactly as the Hero CTA slides under the navbar.
      { threshold: 0, rootMargin: "-320px 0px 10000px 0px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [firstSlice]);

  const getStartUrl = () => {
    if (startAnchorId) {
      return `#${startAnchorId}`;
    }

    const isInternal = data?.start_link?.link_type === "Document";
    if (isInternal) {
      const startInternalUrl = linkResolver(
        data?.start_link as PrismicDocuemntLink,
      );
      return startInternalUrl ?? "https://app.shine.fr/register";
    } else {
      return data?.start_link?.url;
    }
  };
  const startUrl = getStartUrl();

  const hasMatchingHero = useMemo(() => {
    return (
      version === "v4" &&
      typeof startUrl === "string" &&
      isHeroWithMatchingButton(
        firstSlice,
        data?.start_label as string,
        startUrl,
      )
    );
  }, [firstSlice, data?.start_label, startUrl, version]);

  const showSignup = !hasMatchingHero || !isFirstSliceInView;

  const loginUrl = data?.login_link?.url || ("https://app.shine.fr/" as string);

  if (!data) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        `[SiteNavbar] Requested navbar version "${version}" but no content was found in Prismic.`,
      );
    }
    // Attempt fallback only once
    if (version !== "v2") {
      return <SiteNavbar version="v2" />;
    }
    return null; // Final failure: no v2 data available
  }

  return (
    <Fragment>
      {/* Used to know if the user has scrolled */}
      <div ref={ref} />

      <Container
        position={isSticky ? "sticky" : "relative"}
        size="full"
        top={0}
        background="white"
        zIndex="navbar"
        id="site-navbar"
        h="64px"
        justifyContent="center"
      >
        {banner && <SiteBanner open={scrollTop} {...banner} />}

        <MobileDrawer
          isOpen={drawer.isOpen}
          onClose={drawer.onClose}
          data={data as NonNullable<SiteNavbarData>}
          headerSections={headerSections!}
          accountSegmentNavbarLogo={accountSegmentNavbarLogo}
          version={version}
        />

        <Container
          as="nav"
          size="block"
          display="flex"
          alignItems={{ base: "center", navbar: "flex-start" }}
          justifyContent="space-between"
          paddingY={{ base: "space-16", navbar: "space-8" }}
          paddingX={{ base: "space-16", navbar: "space-58", "2xl": 0 }}
          borderBottom={{ base: "1px solid", navbar: "none" }}
          borderColor="grey-0"
          gridGap={{ base: "space-16", navbar: "0" }}
        >
          <IconButton
            as={GatsbyLink}
            aria-label="Shine logo"
            variant="ghost"
            flexShrink="0"
            marginY="auto"
            paddingY="0"
            icon={
              version === "v5" && accountSegmentNavbarLogo?.url ? (
                <Image
                  src={accountSegmentNavbarLogo.url}
                  alt={accountSegmentNavbarLogo.alt || "Shine logo"}
                  width={{ base: "96px", navbar: "130px" }}
                  height="auto"
                />
              ) : (
                <ShiIcon
                  name="ShineLogo"
                  width={{ base: "96px", navbar: "130px" }}
                  height="auto"
                />
              )
            }
            to="/"
            _hover={{
              backgroundColor: "initial",
            }}
          />

          <Spacer display={{ base: "block", navbar: "none" }} maxWidth="100%" />

          <AnimatePresence>
            {showSignup && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.2 }}
                style={{ display: "flex" }}
              >
                <Button
                  as="a"
                  href={startUrl as string}
                  display={{ base: "block", navbar: "none" }}
                  variant="signup"
                  size="sm"
                  fontSize="font-13"
                  lineHeight="20px"
                  fontWeight="medium"
                  color="btn-signup-text"
                  py="6px !important"
                  px="space-16"
                  pr="space-16"
                >
                  {data.start_label}

                  <chakra.span
                    transition="transform .2s ease-out"
                    marginLeft="space-8"
                    className="element-cta-icon"
                  >
                    →
                  </chakra.span>
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          <IconButton
            display={{ base: "flex", navbar: "none" }}
            aria-label="menu"
            icon={<ShiIcon name="menu" w="20px" h="20px" />}
            onClick={drawer.onOpen}
            variant="icon"
            width="32px"
            height="32px"
            minW="unset" //override default style
            marginRight="-8px" //to align with right end
          />

          <Flex
            alignItems="center"
            flexGrow={1}
            display={{ base: "none", navbar: "flex" }}
          >
            <MenuContext.Provider value={{ state, dispatch }}>
              <HStack
                ml={{ base: "space-24", lg: "space-40", xl: "space-80" }}
                fontSize="font-15"
                spacing="0"
                flexGrow="1"
              >
                {headerSections?.map((slice, index) => {
                  return (
                    <SiteHeaderSection
                      key={index}
                      data={slice}
                      menuIndex={index}
                    />
                  );
                })}
              </HStack>
            </MenuContext.Provider>

            <Spacer />

            <Link
              fontWeight="medium"
              variant="link-nav"
              href={loginUrl}
              whiteSpace="nowrap"
            >
              {data.login_label}
            </Link>

            <AnimatePresence>
              {showSignup && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                >
                  <ShiButton
                    onClick={(e) => {
                      const clickInfo: ClickInfo = {
                        label: data.start_label,
                        platform: "web-desktop",
                        block: "site_navbar",
                        fromLocation: location,
                        toUrl: startUrl as string,
                      };
                      startUrl
                        ? onClickStartWithLink(e, visitorContext, clickInfo)
                        : onClickNewAccount(e, visitorContext, clickInfo);
                    }}
                    variant="signup"
                    marginLeft="space-24"
                    flexShrink="0"
                  >
                    {data.start_label}
                  </ShiButton>
                </motion.div>
              )}
            </AnimatePresence>
          </Flex>
        </Container>
      </Container>
    </Fragment>
  );
};

export default SiteNavbar;
